import { makeStyles } from '@fluentui/react-components';
import { tokens, useFluentTokens } from '../../theme/fluent';

const AppLayoutStyles = makeStyles({
  itemRoot: {
    display: 'flex',
    alignItems: 'center',
    rowGap: tokens.spacingVerticalXXS,
    columnGap: tokens.spacingHorizontalXXS,
    paddingLeft: tokens.spacingHorizontalSNudge,
    paddingRight: tokens.spacingHorizontalXS,
    position: 'relative',
  },
  indicator: {
    width: '4px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transform: 'translateX(0px)',
  },
  indicatorIsActive: {
    width: '4px',
    height: '20px',
    backgroundColor: '#0DF200',
    borderRadius: tokens.borderRadiusSmall,
  },
  menuButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '10px',
    paddingLeft: '10px',
    flex: 1,
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusXXXLarge,
    transition: 'all 0.2s ease',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    marginRight: '8px',
  },
  menuButtonIconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    flexShrink: 0,
  },
  menuButtonLabel: {
    whiteSpace: 'nowrap',
    fontSize: '14px',
    lineHeight: '20px',
  },
  appRoot: {
    height: '100vh',
  },
  appRootInner: {
    display: 'flex',
    height: '100%',
    backgroundColor: 'transparent',
  },
  sideNav: {
    background: 'transparent',
    borderRight: 'none',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
  },
  collapseButtonSection: {
    height: '60px',
    borderBottom: 'none',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '22px',
  },
  collapseButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },
  collapseSeparator: {
    height: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    margin: '0 10px',
  },
  navItem: {
    flexGrow: 1,
    overflow: 'auto',
    minHeight: 0,
  },
  navItemCollapsedLine: {
    height: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    margin: '8px 16px',
  },
  toolHeader: {
    marginTop: '4px',
    marginBottom: '4px',
  },
  toolHeaderText: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  themeToggleRoot: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 16px',
    marginBottom: '8px',
  },
  themeToggleIconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
  },
  themeToggleIcon: {
    width: '16px',
    height: '16px',
  },
  themeToggleItem: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  themeToggleButton: {
    '&:focus-visible': {
      outline: '2px solid #000',
      outlineOffset: '2px',
    },
  },
  themeToggleItemText: {
    fontSize: '14px',
    lineHeight: '20px',
  },
  powerSection: {
    height: '64px', // Fixed height for both states
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  powerSectionText: {
    color: '#D6D6D6',
    whiteSpace: 'nowrap',
  },
  mainRoot: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
  },
  mainHeaderTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'transparent',
    borderBottom: 'none',
    height: '64px',
    flexShrink: 0,
  },
  brandLogoImage: {
    height: '16px',
    width: 'auto',
    objectFit: 'contain',
  },
  brandSeparator: {
    width: '1px',
    height: '24px',
    backgroundColor: '#D7D8D8',
  },
  brandText: {
    fontSize: '16px',
  },
  userMenuTrigger: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: '10px',
    borderRadius: tokens.borderRadiusLarge,
    cursor: 'pointer',
  },
  mainContent: {
    flexGrow: 1,
    overflow: 'hidden',
    borderTopLeftRadius: '16px',
  },
  chatToggleSection: {
    display: 'flex',
    justifyContent: 'center',
    padding: '8px 0',
  },
  chatToggleIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: '10px',
  },
});

export const useAppLayoutStyles = () => {
  const classes = AppLayoutStyles();
  const { modeTokens } = useFluentTokens();

  const appRootStyle: React.CSSProperties = {
    background: modeTokens.brandJewelGradient,
  };

  const mainContentBackgroundStyle: React.CSSProperties = {
    backgroundColor: modeTokens.colorNeutralBackground3,
  };

  const pureWhiteColor: React.CSSProperties = {
    color: modeTokens.colorNeutralForegroundStaticInverted,
  };

  return { classes, appRootStyle, mainContentBackgroundStyle, pureWhiteColor };
};
